import React, { useCallback, memo } from 'react';
import { OPTIMISTIC_ID_PREFIX } from '@atlassian/jira-business-list-handlers/src/common/constants.tsx';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl/src/v2/types.tsx';
import { toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { isBaseLevel } from '@atlassian/jira-software-roadmap-model/src/hierarchy/index.tsx';
import { EXPLICIT } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/common/constants/date.tsx';
import type {
	OnChartItemClick,
	OnChartItemUpdate,
	Placeholder,
} from '@atlassian/jira-software-roadmap-timeline-table-kit/src/common/types/chart-item.tsx';
import { RoadmapChartItemContent } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/ui/chart-item-content/main.tsx';
import { CHART_BAR_RESIZE } from '@atlassian/jira-software-roadmap-utils/src/utils/metrics/common/constants.tsx';
import { DndLongTaskMetric } from '../../../common/ui/metrics/dnd-metrics/index.tsx';
import { getColor } from '../../../common/utils/get-color.tsx';
import { useTimelineFeatures } from '../../../controllers/features-context/index.tsx';
import type { TimelineViewIssue } from '../../../types.tsx';
import { renderBarContent } from './bar-content/index.tsx';
import { START_DATE_LATER_DUE_DATE_MESSAGE_ID } from './constants.tsx';

type ChartItemContentProps = {
	dueDate?: number;
	isExportTable: boolean;
	isHovered: boolean;
	isReadOnly: boolean;
	issue: TimelineViewIssue;
	level: number;
	onItemUpdate: OnChartItemUpdate;
	placeholder: Placeholder;
	setSelectedIssueKey: (arg1: string) => void;
	startDate?: number;
};
type DndLongTaskMetricProps = {
	isDragging: boolean;
	level: number;
};

const DndLongTaskMetricComponent = ({ level, isDragging }: DndLongTaskMetricProps) => (
	<DndLongTaskMetric
		isDragging={isDragging}
		metricType={CHART_BAR_RESIZE}
		extraProps={{ isBaseLevel: isBaseLevel(level) }}
	/>
);

export const ChartItemContent = memo<ChartItemContentProps>((props: ChartItemContentProps) => {
	const {
		isExportTable,
		isHovered,
		isReadOnly,
		issue,
		level,
		onItemUpdate,
		placeholder,
		setSelectedIssueKey,
	} = props;
	let { dueDate, startDate } = props;

	const warnings: MessageDescriptorV2[] = [];
	if (dueDate !== undefined && startDate !== undefined && dueDate < startDate) {
		warnings.push(START_DATE_LATER_DUE_DATE_MESSAGE_ID);
		[startDate, dueDate] = [dueDate, startDate];
	}

	const { status, issueId } = issue;
	const color = getColor(status.categoryId);
	const { issueViewInteraction } = useTimelineFeatures();

	const onUpdate = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type. | TS7006 - Parameter 'param' implicitly has an 'any' type. | TS7006 - Parameter 'analyticEvent' implicitly has an 'any' type.
		(id, param, analyticEvent) => {
			onItemUpdate(toIssueId(id), param, analyticEvent);
		},
		[onItemUpdate],
	);

	const leftClickHandler = useCallback<OnChartItemClick>(
		(_id, meta) => {
			if (issue.id.startsWith(OPTIMISTIC_ID_PREFIX)) {
				return;
			}
			if (issueViewInteraction === 'openNewTab' || meta.withCmd || meta.withShift) {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(`/browse/${issue.key}`, '_blank');
				return;
			}
			setSelectedIssueKey(issue.key);
		},
		[issue.id, issue.key, issueViewInteraction, setSelectedIssueKey],
	);

	return (
		<RoadmapChartItemContent
			renderBarContent={isExportTable ? undefined : renderBarContent}
			onUpdate={onUpdate}
			level={level}
			isHovered={isHovered}
			id={issueId}
			color={color}
			startDate={startDate}
			dueDate={dueDate}
			startDateType={EXPLICIT}
			dueDateType={EXPLICIT}
			placeholder={placeholder}
			DndLongTaskMetric={DndLongTaskMetricComponent}
			isReadOnly={isReadOnly}
			onLeftClick={leftClickHandler}
			warnings={warnings}
		/>
	);
});
